import type { Recipe, RecipeComponent } from '../../../services/recipes/recipesService';

// Match the recipe app's translation fallback and accent-insensitive phrase search.
export function translatedText(value: unknown, language: string): string {
  if (typeof value === 'string') return value.trim();
  if (!value || typeof value !== 'object') return '';
  const texts = value as Record<string, unknown>;
  for (const key of Array.from(new Set([language, language.split('-')[0], 'en', ...Object.keys(texts).sort()]))) {
    if (typeof texts[key] === 'string' && (texts[key] as string).trim()) return (texts[key] as string).trim();
  }
  return '';
}

export function recipeName(recipe: Recipe, language: string): string {
  return translatedText(recipe.nameTranslations, language) || recipe.menuItemName || '';
}

export function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
}

function translationMatches(value: unknown, query: string): boolean {
  const values = typeof value === 'string' ? [value] : value && typeof value === 'object' ? Object.values(value) : [];
  return values.some(text => typeof text === 'string' && normalizeSearch(text).includes(query));
}

export function recipeMatchesSearch(recipe: Recipe, query: string): boolean {
  const normalized = normalizeSearch(query);
  return !normalized || translationMatches(recipe.nameTranslations, normalized) ||
    translationMatches(recipe.menuItemName, normalized) ||
    (recipe.components ?? []).some(component => translationMatches(component.nameTranslations, normalized));
}

export type ViewerService = 'ALL' | 'FOOD' | 'DRINKS';
export const VIEWER_SORTS = ['menu-order', 'a-z', 'z-a', 'newest', 'category'] as const;
export type ViewerSort = typeof VIEWER_SORTS[number];

export function filterViewerRecipes(recipes: Recipe[], options: {
  query: string; category: string; service: ViewerService; sort: ViewerSort; language: string;
}): Recipe[] {
  const supported = recipes.some(recipe => Array.isArray(recipe.serviceTypes));
  const service = supported ? options.service : 'ALL';
  const filtered = recipes.filter(recipe => recipeMatchesSearch(recipe, options.query) &&
    (!options.category || (recipe.categoryId ?? recipe.category) === options.category) &&
    (service === 'ALL' || recipe.serviceTypes?.includes(service)));
  return filtered.sort((a, b) => {
    switch (options.sort) {
      case 'a-z': return recipeName(a, options.language).localeCompare(recipeName(b, options.language));
      case 'z-a': return recipeName(b, options.language).localeCompare(recipeName(a, options.language));
      case 'newest': return (new Date(b.updatedAt).getTime() || 0) - (new Date(a.updatedAt).getTime() || 0);
      case 'category': return (a.category ?? '').localeCompare(b.category ?? '');
      default: return (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER);
    }
  });
}

export function orderedRecipeRows<T extends { position?: number }>(rows: T[] = []): T[] {
  return [...rows].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

// Never route by array position: reordering/removing a component must not redirect its old link.
// Missing or duplicate IDs cannot safely own remote notes or a persistent detail destination.
export function componentRouteKey(recipe: Recipe, component: RecipeComponent): string | null {
  return component.id && recipe.components.filter(item => item.id === component.id).length === 1 ? component.id : null;
}

export function resolveViewerComponent(recipe: Recipe, key: string | undefined): RecipeComponent | null {
  if (!key) return null;
  const matches = (recipe.components ?? []).filter(component => component.id === key);
  return matches.length === 1 ? matches[0] : null;
}
